/**
 * FairRuleCards.tsx
 *
 * Displays all FAIR Rules as expandable cards
 * Groups by pillar with compliance summary
 */

import React from 'react';
import { FAIRRuleCard, FairRule } from './FAIRRuleCard';

interface ValidationFinding {
  ruleId: string;
  severity: 'Info' | 'Warning' | 'Violation';
  message: string;
  path?: string;
}

interface PillarCompliance {
  pillar: 'F' | 'A' | 'I' | 'R';
  percent: number;
  passed: number;
  total: number;
}

interface RuleResult {
  ruleId: string;
  state: 'pass' | 'fail' | 'unknown' | 'not_applicable';
  evidenceIds?: string[];
  notApplicableReason?: string;
  evaluatedBy?: 'shacl' | 'supplementary' | 'none';
}

interface FairRuleCardsProps {
  rules: FairRule[];
  validation: {
    findings: ValidationFinding[];
    summaryByPillar: PillarCompliance[];
  };
  ruleResults: RuleResult[];
  onRuleClick?: (rule: FairRule) => void;
}

export function FairRuleCards({ rules, validation, ruleResults, onRuleClick }: FairRuleCardsProps) {
  // Group rules by pillar
  const rulesByPillar = rules.reduce((acc, rule) => {
    if (!acc[rule.pillar]) {
      acc[rule.pillar] = [];
    }
    acc[rule.pillar].push(rule);
    return acc;
  }, {} as Record<string, FairRule[]>);

  // Get passed rules from validation
  const complianceMap = validation.summaryByPillar.reduce((acc, c) => {
    acc[c.pillar] = { passed: c.passed, total: c.total, percent: c.percent };
    return acc;
  }, {} as Record<string, { passed: number; total: number; percent: number }>);

  // Use rule-level states if available, otherwise infer from pillar compliance
  const stateById = new Map(ruleResults.map(r => [r.ruleId, r.state]));

  // If ruleResults is empty or undefined, infer pass/fail from pillar percentages
  const passedRules = new Set<string>();

  if (ruleResults.length > 0) {
    // Use explicit rule results
    rules.filter(r => stateById.get(r.id) === 'pass').forEach(r => passedRules.add(r.id));
  } else {
    // Infer from pillar compliance: mark first N rules as passed based on pillar.passed count
    rules.forEach(rule => {
      const pillarData = complianceMap[rule.pillar];
      if (!pillarData) return;

      // Get rules for this pillar
      const pillarRules = rules.filter(r => r.pillar === rule.pillar);
      const ruleIndex = pillarRules.findIndex(r => r.id === rule.id);

      // Mark first N rules as passed (where N = pillar.passed)
      if (ruleIndex < pillarData.passed) {
        passedRules.add(rule.id);
      }
    });
  }

  const pillars: Array<'F' | 'A' | 'I' | 'R'> = ['F', 'A', 'I', 'R'];
  const pillarNames = {
    F: 'Findable',
    A: 'Accessible',
    I: 'Interoperable',
    R: 'Reusable'
  };

  return (
    <div className="fair-rule-cards" style={{ padding: '20px' }}>
      {pillars.map(pillar => {
        const pillarRules = rulesByPillar[pillar] || [];
        const compliance = complianceMap[pillar];

        if (pillarRules.length === 0) return null;

        return (
          <div key={pillar} className="pillar-section" style={{ marginBottom: '32px' }}>
            <div className="pillar-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <h2 style={{ fontSize: '24px', fontWeight: '700' }}>
                {pillar} - {pillarNames[pillar]}
              </h2>

              {compliance && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600' }}>
                    {compliance.passed}/{compliance.total} rules passed
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: compliance.percent >= 75 ? '#22c55e' :
                           compliance.percent >= 50 ? '#f97316' : '#ef4444'
                  }}>
                    {Math.round(compliance.percent)}% compliance
                  </div>
                  {(() => {
                    const naCount = pillarRules.filter(r => stateById.get(r.id) === 'not_applicable').length;
                    return naCount > 0 ? (
                      <div style={{ fontSize: '12px', color: '#6366f1', marginTop: '2px' }}>
                        {naCount} N/A (excluded from score)
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>

            <div className="pillar-rules">
              {pillarRules.map(rule => {
                const ruleResult = ruleResults.find(r => r.ruleId === rule.id);
                const status = ruleResult?.state || (passedRules.has(rule.id) ? 'pass' : 'unknown');
                return (
                  <FAIRRuleCard
                    key={rule.id}
                    rule={rule}
                    passed={passedRules.has(rule.id)}
                    status={status}
                    notApplicableReason={ruleResult?.notApplicableReason}
                    evaluatedBy={ruleResult?.evaluatedBy}
                    onViewEvidence={onRuleClick ? () => onRuleClick(rule) : undefined}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
